/**
 * The customer knowledge base's API client.
 *
 * In the dashboard rather than in `@nab/shared`, for two reasons.
 *
 * `shared/` is **vendored** from nab-agent and checked byte-for-byte, so anything added there
 * fails the drift check and is overwritten by the next sync — which is exactly what happened to
 * the first version of this file.
 *
 * And `shared/` already exports `KbDocument`, `fetchKbDocuments` and `uploadKbDocument` for the
 * *employee* knowledge base, which has a different shape: snake_case fields, OCR confidence, a
 * review queue, retired versions, downloadable forms. Two different things under one name in one
 * module is how a screen ends up reading `published_by` when it meant `published`.
 *
 * Nothing outside this dashboard needs these, so they live beside it.
 */

import { ApiError, type ApiOptions } from "@nab/shared";

const BASE = "/admin/v1";

export interface CustomerKbDocument {
  rowId: string;
  docId: string;
  version: string;
  title: string;
  language: string;
  topic: string | null;
  validFrom: string | null;
  validTo: string | null;
  status: string;
  statusDetail: string | null;
  indexedChunks: number;
  /** `qa` when the file was read as question-and-answer pairs, `prose` otherwise. */
  kind?: string;
  published: boolean;
  sourceFilename: string;
  sizeBytes: number;
  uploadedBy: string;
  updatedAt: string;
}

export interface CustomerKbLimits {
  maxBytes: number;
  supportedSuffixes: string[];
  /** What a document can be converted to. The menu is built from this rather than hardcoded. */
  exportFormats: string[];
}

export interface CustomerKbPassage {
  docId: string;
  title: string;
  section: string | null;
  text: string;
}

export interface CustomerKbAnswer {
  /** `ok` or `no_grounding`. The second means the bot would refuse — the useful signal. */
  status: string;
  passages: CustomerKbPassage[];
  collectionsSearched: string[];
  latencyMs: number;
}

function headersFor(options: ApiOptions): Record<string, string> {
  const headers: Record<string, string> = { Accept: "application/json" };
  // Development identity. Deployed, the session cookie carries it and the server ignores these.
  if (options.employeeId) headers["X-NAB-Employee-Id"] = options.employeeId;
  if (options.role) headers["X-NAB-Role"] = options.role;
  return headers;
}

async function unwrap<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function call<T>(
  path: string,
  options: ApiOptions & { method?: string; body?: unknown } = {},
): Promise<T> {
  const { method = "GET", body, baseUrl, signal, ...identity } = options;
  const headers = headersFor(identity);
  if (body !== undefined) headers["Content-Type"] = "application/json";
  return unwrap<T>(
    await fetch(`${baseUrl ?? "/api"}${BASE}${path}`, {
      method,
      headers,
      credentials: "same-origin",
      signal: signal ?? null,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  );
}

export const fetchKbLimits = (o?: ApiOptions): Promise<CustomerKbLimits> =>
  call("/kb/limits", o);

export const fetchCustomerKbDocuments = (
  params: { q?: string; limit?: number; offset?: number } = {},
  o?: ApiOptions,
): Promise<{ documents: CustomerKbDocument[]; total: number }> => {
  const query = new URLSearchParams();
  if (params.q) query.set("q", params.q);
  if (params.limit !== undefined) query.set("limit", String(params.limit));
  // `!== undefined`, not truthiness: offset 0 is the first page and must still be sent.
  if (params.offset !== undefined) query.set("offset", String(params.offset));
  const suffix = query.toString() ? `?${query}` : "";
  return call(`/kb${suffix}`, o);
};

/**
 * Upload a document. Multipart, and not through `call`.
 *
 * `call` sets `Content-Type: application/json`. A multipart upload needs the browser to set the
 * boundary itself, and overriding that header produces a body the server cannot parse.
 */
export async function uploadCustomerKbDocument(
  file: File,
  metadata: Record<string, string>,
  options: ApiOptions = {},
): Promise<CustomerKbDocument> {
  const { baseUrl, signal, ...identity } = options;
  const form = new FormData();
  form.append("file", file);
  for (const [key, value] of Object.entries(metadata)) {
    if (value) form.append(key, value);
  }
  return unwrap<CustomerKbDocument>(
    await fetch(`${baseUrl ?? "/api"}${BASE}/kb`, {
      method: "POST",
      headers: headersFor(identity),
      body: form,
      credentials: "same-origin",
      signal: signal ?? null,
    }),
  );
}

export const setKbPublished = (
  rowId: string,
  published: boolean,
  o?: ApiOptions,
): Promise<CustomerKbDocument> =>
  call(`/kb/${encodeURIComponent(rowId)}/published`, {
    ...o,
    method: "POST",
    body: { published },
  });

/**
 * Delete a document, as opposed to withdrawing it.
 *
 * Withdrawing leaves it on the list; deleting takes it off. Neither erases the passages — they
 * are retired, so an audit entry citing a chunk still resolves — unless `purge` is asked for,
 * which is the one action that makes an old citation stop resolving. The screen does not offer
 * `purge`: it is a thing to do deliberately from the command line, with the consequence in front
 * of you, not a checkbox beside a delete button.
 */
export const deleteKbDocument = (
  rowId: string,
  o?: ApiOptions,
): Promise<CustomerKbDocument> =>
  call(`/kb/${encodeURIComponent(rowId)}`, { ...o, method: "DELETE" });

/**
 * Read a file with the ingestion parsers and get it back as Markdown or JSON.
 *
 * Multipart in, text out, and not through `call` for the same reason the upload is not: the
 * browser has to set the multipart boundary itself, and the response is a file rather than JSON.
 *
 * Returns the text and the name to save it under. The filename comes from `Content-Disposition`
 * when the browser will let us read it, and falls back to the uploaded name — these are Arabic
 * filenames as often as not, so the header is RFC 5987 encoded and has to be decoded back.
 */
export async function convertKbDocument(
  file: File,
  format: string,
  metadata: Record<string, string> = {},
  options: ApiOptions = {},
): Promise<{ text: string; filename: string }> {
  const { baseUrl, signal, ...identity } = options;
  const form = new FormData();
  form.append("file", file);
  form.append("format", format);
  for (const [key, value] of Object.entries(metadata)) {
    if (value) form.append(key, value);
  }

  const response = await fetch(`${baseUrl ?? "/api"}${BASE}/kb/convert`, {
    method: "POST",
    headers: headersFor(identity),
    body: form,
    credentials: "same-origin",
    signal: signal ?? null,
  });

  if (!response.ok) {
    let detail: unknown;
    try {
      detail = (await response.json())?.detail;
    } catch {
      /* a non-JSON error body; the status carries the meaning */
    }
    throw new ApiError(`Request failed with ${response.status}`, response.status, detail);
  }

  const fallback = `${file.name.replace(/\.[^.]+$/, "")}.${format}`;
  return { text: await response.text(), filename: filenameFrom(response) ?? fallback };
}

/** The name in `Content-Disposition`, or null when the header is absent or unreadable. */
function filenameFrom(response: Response): string | null {
  const header = response.headers.get("Content-Disposition");
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      return null;
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? null;
}

export const askKb = (
  question: string,
  language?: string,
  o?: ApiOptions,
): Promise<CustomerKbAnswer> =>
  call("/kb/ask", {
    ...o,
    method: "POST",
    body: { question, ...(language ? { language } : {}) },
  });
